import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { IoTrendingUp, IoConstructOutline, IoWalletOutline } from 'react-icons/io5';


const FeaturesSection = styled.section`
  padding: 5rem 2rem;
  background: ${({ theme }) => theme.colors.opacityColor};

  @media (max-width: 768px) {
    padding: 3rem 1rem;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;

  opacity: 0;
  transform: translateY(40px);
  transition: opacity 0.8s ease-out, transform 0.8s ease-out;

  &.visible {
    opacity: 1;
    transform: translateY(0);
  }
`;

const SectionLabel = styled.span`
  display: inline-block;
  padding: 0.5rem 1.25rem;
  background: ${({ theme }) => theme.colors.activeColor}20;
  color: ${({ theme }) => theme.colors.activeColor};
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  border-radius: 50px;
  margin-bottom: 1.5rem;
`;

const SectionTitle = styled.h2`
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 700;
  color: ${({ theme }) => theme.colors.headline};
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    font-size: 1.75rem;
  }
`;

const AccentText = styled.span`
  color: ${({ theme }) => theme.colors.activeColor};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    max-width: 500px;
    margin: 0 auto;
  }
`;

const FeatureCard = styled.div`
  background: ${({ theme }) => theme.colors.cardBG};
  padding: 2.5rem;
  border-radius: ${({ theme }) => theme.radii.md};
  box-shadow: ${({ theme }) => theme.shadows.md};
  border-left: 3px solid transparent;

  opacity: 0;
  transform: translateY(40px);
  transition: opacity 0.6s ease-out, transform 0.6s ease-out, box-shadow 0.3s ease, border-color 0.3s ease;

  &.visible {
    opacity: 1;
    transform: translateY(0);
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: ${({ theme }) => theme.shadows.lg};
    border-left-color: ${({ theme }) => theme.colors.activeColor};
  }

  &:nth-child(1) { transition-delay: 0s; }
  &:nth-child(2) { transition-delay: 0.1s; }
  &:nth-child(3) { transition-delay: 0.2s; }
`;

const IconWrapper = styled.div`
  width: 60px;
  height: 60px;
  background: ${({ theme }) => theme.colors.activeColor}15;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
  color: ${({ theme }) => theme.colors.activeColor};
`;

const FeatureTitle = styled.h3`
  font-size: 1.35rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.headline};
  margin-bottom: 1rem;
`;

const FeatureDescription = styled.p`
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.paragraph};
  line-height: 1.7;
`;

const features = [
  {
    icon: <IoTrendingUp size={28} />,
    title: 'Rent Estimates That Reflect Your Market',
    description: 'We run every property through AI with local MLS comps. A 3/2 in Tampa rents differently than a 3/2 in Memphis \u2014 generic tools use national averages.'
  },
  {
    icon: <IoConstructOutline size={28} />,
    title: 'CapEx by Component, Not a Flat 5%',
    description: "A 2-year-old roof doesn't need the same reserves as a 15-year-old one. We calculate reserves for roof, HVAC, water heater, and appliances based on actual age and replacement cost."
  },
  {
    icon: <IoWalletOutline size={28} />,
    title: 'Every Expense, Every Line Item',
    description: "Vacancy, management, maintenance, insurance, taxes, closing costs, and component-level CapEx — all factored into COC%, IRR, and 5-year projections. The numbers you see are the numbers you get."
  }
];

const Features = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.05 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    // Fallback for mobile - trigger after 1s if still not visible
    const fallback = setTimeout(() => setIsVisible(true), 1500);

    return () => {
      observer.disconnect();
      clearTimeout(fallback);
    };
  }, []);

  return (
    <FeaturesSection ref={sectionRef}>
      <Container>
        <SectionHeader className={isVisible ? 'visible' : ''}>
          <SectionLabel>What Makes The Difference</SectionLabel>
          <SectionTitle>
            Three Things Other Calculators <AccentText>Miss</AccentText>
          </SectionTitle>
        </SectionHeader>
        <Grid>
          {features.map((feature, index) => (
            <FeatureCard key={index} className={isVisible ? 'visible' : ''}>
              <IconWrapper>{feature.icon}</IconWrapper>
              <FeatureTitle>{feature.title}</FeatureTitle>
              <FeatureDescription>{feature.description}</FeatureDescription>
            </FeatureCard>
          ))}
        </Grid>
      </Container>
    </FeaturesSection>
  );
};

export default Features;
